import type { ApiSuccessResponse } from './api'
import type { AssistantDoctorLink } from './assistant'

export type AuthUser = {
  id: string
  salutation: string
  first_name: string
  surname: string
  email: string
  phone_number: string | null
  image: string | null
  status: string
  rejection_reason?: string | null
  is_active: boolean
  /** Profile-reviewer capability flag — required with doctors-* permissions. */
  can_review_profiles?: boolean
  department_id: string | null
  position_id: string | null
}

export type AuthPermission = {
  id: string
  name: string
}

/** Slim assistant↔doctor flags from /auth/me (dual-gate with Spatie permissions). */
export type AuthAssistantLink = Pick<
  AssistantDoctorLink,
  | 'id'
  | 'assistant_id'
  | 'doctor_id'
  | 'can_create_cases'
  | 'can_set_global'
  | 'can_manage_review'
  | 'can_update_outcome'
  | 'deleted_at'
>

export type AuthSession = {
  user: AuthUser
  role: string | null
  dashboard_path: string | null
  permissions: AuthPermission[]
  /** Present for assistants — empty for other roles. */
  assistant_links?: AuthAssistantLink[]
  /** Contact shown on pending/rejected doctor account-status page. */
  doctor_support_email?: string
  token?: string
}

export type AuthSessionResponse = ApiSuccessResponse<AuthSession>

export const USER_ROLES = {
  SUPER_ADMIN: 'super-admin',
  PROFILE_REVIEWER: 'reviewer',
  DOCTOR: 'doctor',
  ASSISTANT: 'assistant',
} as const

export type UserRoleSlug = (typeof USER_ROLES)[keyof typeof USER_ROLES]

export const ROLE_DASHBOARD_PATHS: Record<UserRoleSlug, string> = {
  [USER_ROLES.SUPER_ADMIN]: '/admin/dashboard',
  [USER_ROLES.PROFILE_REVIEWER]: '/reviewer/dashboard',
  [USER_ROLES.DOCTOR]: '/dashboard',
  [USER_ROLES.ASSISTANT]: '/assistant/dashboard',
}

export const ROLE_LABELS: Record<UserRoleSlug, string> = {
  [USER_ROLES.SUPER_ADMIN]: 'Super Admin',
  [USER_ROLES.PROFILE_REVIEWER]: 'Profile Reviewer',
  [USER_ROLES.DOCTOR]: 'Doctor',
  [USER_ROLES.ASSISTANT]: 'Assistant',
}

export function resolveDashboardPath(role: string | null | undefined, fallback = '/login'): string {
  if (!role) {
    return fallback
  }

  return ROLE_DASHBOARD_PATHS[role as UserRoleSlug] ?? fallback
}

export function hasPermission(
  permissions: AuthPermission[],
  permission: string,
): boolean {
  return permissions.some((item) => item.name === permission)
}

function activeAssistantLinks(session: AuthSession): AuthAssistantLink[] {
  return (session.assistant_links ?? []).filter((link) => !link.deleted_at)
}

export function assistantHasAnyFlag(
  session: AuthSession | null | undefined,
  flag: keyof Pick<
    AuthAssistantLink,
    'can_create_cases' | 'can_set_global' | 'can_manage_review' | 'can_update_outcome'
  >,
): boolean {
  if (!session || session.role !== USER_ROLES.ASSISTANT) {
    return false
  }

  return activeAssistantLinks(session).some((link) => Boolean(link[flag]))
}

export function assistantLinkForDoctorId(
  session: AuthSession | null | undefined,
  doctorId: string | null | undefined,
): AuthAssistantLink | undefined {
  if (!session || !doctorId) {
    return undefined
  }

  return activeAssistantLinks(session).find((link) => link.doctor_id === doctorId)
}

/**
 * Doctor-profile review requires Spatie permission AND, for profile reviewers,
 * `user.can_review_profiles === true`. Super admins only need the permission.
 * Permission alone is never enough for a profile reviewer.
 */
export function canReviewDoctorProfiles(
  session: AuthSession | null | undefined,
  permission = 'doctors-list',
): boolean {
  if (!session || !hasPermission(session.permissions, permission)) {
    return false
  }

  if (session.role === USER_ROLES.SUPER_ADMIN) {
    return true
  }

  if (session.role !== USER_ROLES.PROFILE_REVIEWER) {
    return false
  }

  return Boolean(session.user.can_review_profiles) && session.user.is_active
}

/**
 * Case actions for assistants: Spatie permission first.
 * Review / outcome still also need the matching doctor pivot flag.
 * Create / update / delete use permission only (pivot create flag unused for now).
 * Doctors / super-admins only need the permission (coarse UI gate).
 */
export function canUseCasePermission(
  session: AuthSession | null | undefined,
  permission: string,
): boolean {
  if (!session || !hasPermission(session.permissions, permission)) {
    return false
  }

  if (session.role !== USER_ROLES.ASSISTANT) {
    return true
  }

  if (!session.user.is_active) {
    return false
  }

  // Read / browse / create-mutate (except review & outcome) — Spatie permission alone.
  // Create is no longer gated by pivot `can_create_cases` (capability UI hidden for now).
  if (
    permission === 'cases-list' ||
    permission === 'cases-show' ||
    permission === 'cases-review-inbox' ||
    permission === 'cases-outcome-view' ||
    permission === 'cases-create' ||
    permission === 'cases-update' ||
    permission === 'cases-delete' ||
    permission === 'cases-restore' ||
    permission === 'cases-publish' ||
    permission === 'cases-schedule' ||
    permission === 'cases-archive' ||
    permission.startsWith('case-files-') ||
    permission.startsWith('case-comments-') ||
    permission.startsWith('case-activity-')
  ) {
    return true
  }

  if (
    permission === 'cases-open-review' ||
    permission === 'cases-close-review' ||
    permission.startsWith('cases-reviewers-')
  ) {
    return assistantHasAnyFlag(session, 'can_manage_review')
  }

  if (permission === 'cases-outcome-update') {
    return assistantHasAnyFlag(session, 'can_update_outcome')
  }

  return true
}
